import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { NotificationService } from '../../../../services/notification.service';
import { AdminService } from '../../../../services/admin.service';

interface StatusMapping {
  status: string;
  cssClass: string;
}

@Component({
    selector: 'app-tlreditorchangerepaired',
    imports: [CommonModule, FormsModule, TableModule],
    templateUrl: './tlreditorchangerepaired.component.html',
    styleUrl: './tlreditorchangerepaired.component.scss',
    providers: [MessageService]
})
export class TlreditorchangerepairedComponent {
    tagNumber: string = '';
  isDataLoaded: boolean = false;
  statusMessage: string = '';
  rawTlrStatusCode: string = ''; // Store raw status code to check for 'R'

  // Operations Data
  operationsData = {
    dateEntered: '',
    operatorCAI: '',
    tlrStatus: { status: '', cssClass: '' } as StatusMapping,
    repairType: '',
    tagNumber: '',
    division: '',
    section: '',
    plant: '',
    shutdownDescription: '',
    associatedPlant: '',
    leakingFrom: '',
    leakingFromOther: '',
    operatingPSI: '',
    operatingTemp: '',
    materialState: '',
    service: '',
    serviceOther: '',
    locationComments: ''
  };

  // Inspections Data
  inspectionsData = {
    dateModified: '',
    inspectorCAI: '',
    permanentRepairDate: '',
    serviceClass: '',
    thicknessMeasure: '',
    systemFlangeRating: '',
    oppisLine: '',
    inspectionISONo: '',
    ewoRequired: '',
    inspectionPlanCompleted: '',
    permanentRepairCompleted: '',
    inspectorComments: ''
  };

  // Operations table data for p-table
  operationsTableData: { key: string; value: string; cssClass?: string }[] = [];

  // Inspections table data for p-table
  inspectionsTableData: { key: string; value: string }[] = [];

  constructor(
    private http: HttpClient,
    private messageService: NotificationService,
    private adminService: AdminService
  ) { }
  searchTag(): void {
    if (!this.tagNumber || this.tagNumber.trim() === '') {
      this.messageService.showError("Please enter a tag number");
      this.isDataLoaded = false;
      this.statusMessage = '';
      return;
    }

    // Call the API to get TLR data
    this.adminService.getTLREditorChangeRepairedTLRInstalled(this.tagNumber).subscribe({
      next: (response: any) => {
        if (response || response.length > 0) {
          this.isDataLoaded = true;
          this.statusMessage = response.message || '';

          // Map the response to your data objects
          this.mapResponseToData(response);

          this.messageService.showSuccess('TLR data found for tag number: ' + this.tagNumber);
        } else {
          this.isDataLoaded = false;
          this.statusMessage = 'No TLR found with tag number ' + this.tagNumber;
          this.messageService.showInfo('No TLR found with tag number ' + this.tagNumber);
        }
      },
      error: (error: any) => {
        console.error('Error fetching TLR data:', error);
        this.isDataLoaded = false;
        this.statusMessage = 'Error fetching TLR data';
        this.messageService.showError('Error fetching TLR data: ' + (error.message || 'Unknown error'));
      }
    });
  }

  // Map API response to component data correctly once we get response from API
  mapResponseToData(data: any): void {
    // Map operations data from the nested operations object
    const operationsData = data.operations || {};
    // Store raw status code for conditional logic
    this.rawTlrStatusCode = operationsData.tlrStatus || '';
    this.operationsData = {
      dateEntered: operationsData.opDateEntered || '',
      operatorCAI: operationsData.operatorCAI || '',
      tlrStatus: this.mapStatusAndCssClass(operationsData.tlrStatus || ''),
      repairType: operationsData.repairType || '',
      tagNumber: operationsData.tagNumber || this.tagNumber,
      division: operationsData.division || '',
      section: operationsData.section || '',
      plant: operationsData.plant || '',
      shutdownDescription: operationsData.shutdownDescription || '',
      associatedPlant: operationsData.associatedPlant || '',
      leakingFrom: operationsData.leakingFrom || '',
      leakingFromOther: operationsData.leakingFromOther || '',
      operatingPSI: operationsData.operatingPSI || '',
      operatingTemp: operationsData.operatingTemp || '',
      materialState: operationsData.materialState || '',
      service: operationsData.service || '',
      serviceOther: operationsData.serviceOther || '',
      locationComments: operationsData.opComments || ''
    };

    // Map inspections data from the nested inspections object
    const inspectionsData = data.inspections || {};
    this.inspectionsData = {
      dateModified: inspectionsData.inspectionDate || '',
      inspectorCAI: inspectionsData.inspectionCai || '',
      permanentRepairDate: inspectionsData.permanentRepairDate || '',
      serviceClass: inspectionsData.serviceClass || '',
      thicknessMeasure: inspectionsData.thicknessMeasure || '',
      systemFlangeRating: inspectionsData.systemFlangeRating || '',
      oppisLine: inspectionsData.oppis ? 'Yes' : 'No',
      inspectionISONo: inspectionsData.inspectionISONo || '',
      ewoRequired: inspectionsData.ewoRequired ? 'Yes' : 'No',
      inspectionPlanCompleted: inspectionsData.inspPlanCompleted ? 'Yes' : 'No',
      permanentRepairCompleted: inspectionsData.permRepairCompleted ? 'Yes' : 'No',
      inspectorComments: inspectionsData.inspectorComments || ''
    };

    // Populate table data for p-tables
    this.populateTableData();
  }

  // Method to map TLR status codes to readable text and CSS class
  mapStatusAndCssClass(statusCode: string): StatusMapping {
    const statusCode2 = statusCode || '';
    let status = '';
    let cssClass = '';

    // Handle both raw codes (I, P, R) and full status text (Installed, Pre Installation, Removed)
    if (statusCode2 === 'I' || statusCode2 === 'Installed') {

      status = 'Installed';
      cssClass = 'green';
    } else if (statusCode2 === 'P' || statusCode2 === 'Pre Installation') {
      status = 'Pre Installation';
      cssClass = 'blue';
    } else if (statusCode2 === 'R' || statusCode2 === 'Removed') {
      status = 'Removed';
      cssClass = 'red';
    } else {
      // Default case
      status = statusCode2;
      cssClass = '';
    }

    return { status, cssClass };
  }

  // Method to populate table data arrays for p-tables
  populateTableData(): void {
    // Populate operations table data
    this.operationsTableData = [
      { key: 'Date Entered Into System', value: this.operationsData.dateEntered },
      { key: 'Operator Eamil', value: this.operationsData.operatorCAI },
      { key: 'TLR Status', value: this.operationsData.tlrStatus.status, cssClass: this.operationsData.tlrStatus.cssClass },
      { key: 'Repair Type', value: this.operationsData.repairType },
      { key: 'Tag Number', value: this.operationsData.tagNumber },
      { key: 'Division', value: this.operationsData.division },
      { key: 'Section', value: this.operationsData.section },
      { key: 'Plant', value: this.operationsData.plant },
      { key: 'Shutdown Description', value: this.operationsData.shutdownDescription },
      { key: 'Associated Plant - Shutdown', value: this.operationsData.associatedPlant },
      { key: 'Leaking From', value: this.operationsData.leakingFrom },
      { key: 'Other Leak Type specified here', value: this.operationsData.leakingFromOther },
      { key: 'Operating PSI', value: this.operationsData.operatingPSI },
      { key: 'Operating Temp', value: this.operationsData.operatingTemp },
      { key: 'Vapor/Liquid', value: this.operationsData.materialState },
      { key: 'Service', value: this.operationsData.service },
      { key: 'Other Service', value: this.operationsData.serviceOther },
      { key: 'Location / Comments', value: this.operationsData.locationComments }
    ];


    const tlrStatusRow = this.operationsTableData.find(item => item.key === 'TLR Status');


    // Populate inspections table data
    this.inspectionsTableData = [
      { key: 'Date Modified', value: this.inspectionsData.dateModified },
      { key: 'Inspector Email', value: this.inspectionsData.inspectorCAI },
      { key: 'Permanent Repair Date', value: this.inspectionsData.permanentRepairDate },
      { key: 'Service Class', value: this.inspectionsData.serviceClass },
      { key: 'Thickness Measure', value: this.inspectionsData.thicknessMeasure },
      { key: 'System Flange Rating', value: this.inspectionsData.systemFlangeRating },
      { key: 'OPPIS Line?', value: this.inspectionsData.oppisLine },
      { key: 'Inspection ISO No.', value: this.inspectionsData.inspectionISONo },
      { key: 'EWO Required', value: this.inspectionsData.ewoRequired },
      { key: 'Inspection Plan Completed', value: this.inspectionsData.inspectionPlanCompleted },
      { key: 'Permanent Repair Completed', value: this.inspectionsData.permanentRepairCompleted },
      { key: 'Inspector Comments', value: this.inspectionsData.inspectorComments }
    ];
  }

  // In the future, this method would call your API service
  getTagData(tagNumber: string) {
    // This would be replaced with an actual API call
    // return this.http.get(`${this.apiBaseUrl}/api/tlr/${tagNumber}`);
  }

  // Example of mapping API response to your UI models
  mapOperationsData(response: any) {
    // This would map API response fields to your UI model
    return {
      dateEntered: response.dateEntered,
      operatorCAI: response.operatorCAI,
      // ... map other fields
    };
  }

  mapInspectionsData(response: any) {
    // This would map API response fields to your UI model
    return {
      dateModified: response.dateModified,
      inspectorCAI: response.inspectorCAI,
      // ... map other fields
    };
  }

  /**
   * Updates the TLR status from Removed to Installed
   */
  updateStatusToInstalled(): void {
    if (!this.tagNumber || this.tagNumber.trim() === '') {
      this.messageService.showError('Tag number is required');
      return;
    }

    // Call the admin service to update the status
    this.adminService.postRepairChangeTLRStatus(this.tagNumber).subscribe({
      next: (response) => {
        if (response && response.success !== false) {
          this.messageService.showSuccess(`Status updated successfully for tag ${this.tagNumber}`);
          // Refresh the data to show updated status
          this.searchTag();
        } else {
          this.messageService.showError(response?.error || 'Failed to update status');
        }
      },
      error: (error) => {
        console.error('Error updating TLR status:', error);
        this.messageService.showError(`Error updating status: ${error.message || 'Unknown error'}`);
      }
    });
  }
}
